/**
 * The positional detail a team rating rejection carries when a squad cannot
 * form a legal team, and the copy that names which position is wrong and by
 * how much.
 *
 * `error.invalidPositionCounts` is not described by `types/api/generated.ts`:
 * the generated types are rebuilt from the deployed spec, and the API change
 * that emits this detail has not shipped yet. It is therefore read and checked
 * at runtime and treated as absent unless proven present, so the same code
 * works against an API that sends it and one that does not.
 */

/** One position whose count differs from the one a legal squad requires. */
export type InvalidPositionCount = {
  position: string;
  required: number;
  actual: number;
};

const POSITION_PLURAL_LABELS: Record<string, string> = {
  GK: 'goalkeepers',
  DEF: 'defenders',
  MID: 'midfielders',
  FWD: 'forwards'
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isInvalidPositionCount = (
  value: unknown
): value is InvalidPositionCount =>
  isRecord(value) &&
  typeof value.position === 'string' &&
  POSITION_PLURAL_LABELS[value.position] !== undefined &&
  typeof value.required === 'number' &&
  Number.isFinite(value.required) &&
  typeof value.actual === 'number' &&
  Number.isFinite(value.actual);

/**
 * The code `/team-rating` refuses an illegal squad with.
 *
 * Read as well as the positional counts because it is the half that always
 * arrives: an API predating the counts still names the reason this way, and
 * this is what tells a caller the squad is the problem rather than the network
 * or the backend.
 */
const INVALID_SQUAD_COMPOSITION_CODE = 'INVALID_SQUAD_COMPOSITION';

/**
 * Whether an error body says the squad itself cannot form a legal team.
 *
 * A caller acts very differently on this than on any other failure: the squad
 * is the visitor's to fix, where a refused or unreachable backend is not
 * (FFH-2738).
 */
export const isInvalidSquadComposition = (body: unknown): boolean => {
  if (!isRecord(body) || !isRecord(body.error)) return false;
  return (
    body.error.code === INVALID_SQUAD_COMPOSITION_CODE ||
    parseInvalidPositionCounts(body).length > 0
  );
};

/**
 * Pulls the offending counts out of an error body, keeping only the entries
 * naming a position this client can label. Returns an empty array for a body
 * without them, which is every response an API that predates the change sends.
 */
export const parseInvalidPositionCounts = (
  body: unknown
): InvalidPositionCount[] => {
  if (!isRecord(body) || !isRecord(body.error)) return [];

  const { invalidPositionCounts } = body.error;
  if (!Array.isArray(invalidPositionCounts)) return [];

  return invalidPositionCounts.filter(isInvalidPositionCount);
};

/**
 * A member-facing sentence naming each position and both counts, so the squad
 * can be fixed without guessing. Undefined when nothing could be described,
 * which is the signal to keep the message the API sent.
 */
export const describeInvalidPositionCounts = (
  invalidPositionCounts: InvalidPositionCount[]
): string | undefined => {
  const clauses = invalidPositionCounts.map(
    ({ position, required, actual }) =>
      `${actual} ${POSITION_PLURAL_LABELS[position]} where a squad needs ${required}`
  );

  if (clauses.length === 0) return undefined;

  const lastClause = clauses[clauses.length - 1];
  const subject =
    clauses.length > 1
      ? `${clauses.slice(0, -1).join(', ')} and ${lastClause}`
      : lastClause;

  return `Your squad has ${subject}.`;
};
